import {
  Component,
  HostListener,
  inject,
  OnInit,
  OnDestroy,
  ViewChild,
  PLATFORM_ID,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { ActivatedRoute, Router, RouterModule, NavigationEnd, NavigationExtras } from '@angular/router';
import { Subscription } from 'rxjs';
import { take, map, distinctUntilChanged, filter, catchError, finalize } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { Subject, of } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ModalComponent } from '../modal/modal.component';
import { LocationModalComponent, LocationData } from '../location-modal/location-modal.component';
import { NavigationItem } from '../mobile-menu/mobile-menu.component';
import { SidebarComponent, SidebarHeader } from '../sidebar/sidebar.component';
import { SidebarService } from '../../services/sidebar.service';
import { UserFacade } from '../../../core/store/user/user.facade';
import { LocationFacade } from '../../../core/store/location/location.facade';
import { AuthService } from '../../../core/services/auth.service';
import { User } from '../../../core/models/user.model';
import { ToastService } from '../../services/toast.service';
import { CartService } from '../../services/cart.service';
import { AppState } from '../../../core/store/app.state';
import { unformatCnpj } from '../../../core/utils/cnpj.util';
import {
  selectCartSidebarOpen,
  selectCartHeaderWithCount,
  selectIsCartEmpty,
} from '../../../core/store/cart/cart.selectors';
import { closeCartSidebar } from '../../../core/store/cart/cart.actions';
import { CartPageComponent } from '../cart-page/cart-page.component';
import { CartFacade } from '../../../core/store/cart/cart.facade';
import { HeaderService } from '../../services/header.service';
import { sanitizeSlug, TOP_NAV_HUB_SOLUCOES } from '../../../core/utils/utils';
import { CnpjSelectorComponent } from '../cnpj-selector/cnpj-selector.component';
import { ComboSelectorComponent } from '../combo-selector/combo-selector.component';
import { ExternalChannelExitModalComponent } from '../external-channel-exit-modal/external-channel-exit-modal.component';
import { ProductService } from '../../../core/services/product.service';
import { ThemeService } from '../../services/theme.service';
import { I18nService } from '../../../core/services/i18n.service';
import { I18nPipe } from '../../pipes/i18n.pipe';
import { UManagerSlugService, ChannelData } from '../../../core/services/umanager-slug.service';
import { UtmPreservationService } from '../../../core/services/utm-preservation.service';
import { FixaResource, SelectContractRequest } from '../../../core/models/login.model';
import { ApiService } from '../../../core/services/api.service';
import { applySelectContractToSession } from '../../../core/utils/select-contract-response.util';
import { unblockCartHydration } from '../../../core/utils/cart-hydration.util';
import { ChannelThemeService } from '../../../core/services/channel-theme.service';
import { ChannelConfigService } from '../../../core/services/channel-config.service';
import { OnlyIndicationService } from '../../../core/services/only-indication.service';
import { MindCatalogStore } from '../../../core/store/mind/mind-catalog.store';
import { CategoryHelperService } from '../../services/category-helper.service';
import { categoriesForCampaign } from '../../../core/campaign/campaign-contract';
import { CampaignScopeService } from '../../../core/campaign/campaign-scope.service';
import { UserService } from '../../../core/services/user.service';
import { UtmCnpjAccountService } from '../../../core/services/utm-cnpj-account.service';
import { ExternalChannelParentMessageService } from '../../../core/services/external-channel-parent-message.service';
import { ExternalChannelAccessResponse } from '../../../core/models/login.model';
import { ProductCategory } from '../../../core/models/product.model';
import { AvailableOffersFacade } from '../../../core/store/available-offers/available-offers.facade';
import { environment } from '../../../../environments/environment';
import {
  isMceExternalChannelFlow,
  readExternalChannelAccessFromSession,
  readStoredExternalChannelOrigin,
  STORAGE_EXTERNAL_CHANNEL_ACCESS,
  STORAGE_EXTERNAL_CHANNEL_ORIGIN,
} from '../../../core/utils/external-channel-access.util';

@Component({
  selector: 'umkt-header',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatMenuModule,
    RouterModule,
    ModalComponent,
    LocationModalComponent,
    SidebarComponent,
    CartPageComponent,
    CnpjSelectorComponent,
    ComboSelectorComponent,
    ExternalChannelExitModalComponent,
    I18nPipe
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnInit, OnDestroy {
  private sidebarService = inject(SidebarService);
  readonly router = inject(Router);
  private route = inject(ActivatedRoute);
  private userFacade = inject(UserFacade);
  private authService = inject(AuthService);
  private authSubscription?: Subscription;
  private toastService = inject(ToastService);
  private cartService = inject(CartService);
  private store = inject(Store<AppState>);
  private cart = inject(CartFacade);
  private headerService = inject(HeaderService);
  private productService = inject(ProductService);
  private themeService = inject(ThemeService);
  private umanagerSlugService = inject(UManagerSlugService);
  private utmPreservationService = inject(UtmPreservationService);
  private apiService = inject(ApiService);
  private platformId = inject(PLATFORM_ID);
  private i18nService = inject(I18nService);
  private channelThemeService = inject(ChannelThemeService);
  private channelConfigService = inject(ChannelConfigService);
  private onlyIndicationService = inject(OnlyIndicationService);
  private locationFacade = inject(LocationFacade);
  private mindStore = inject(MindCatalogStore);
  private categoryHelper = inject(CategoryHelperService);
  private userService = inject(UserService);
  private utmCnpjAccountService = inject(UtmCnpjAccountService);
  private availableOffersFacade = inject(AvailableOffersFacade);
  private externalChannelParentMessageService = inject(ExternalChannelParentMessageService);

  private readonly SELECTED_CNPJ_KEY = 'umkt_selected_cnpj';
  private readonly REDIRECT_AFTER_LOGIN_KEY = 'redirect_after_login';
  private readonly CLARO_EMPRESAS_HOME_URL = `${environment.mceRedirectUrl}/#/HomeLogada`;
  private readonly SEGMENT_CART_CLEARED_TOAST_FIXA =
    'Seu carrinho foi limpo para continuar com produtos da Claro Fixa.';
  private readonly SEGMENT_CART_CLEARED_TOAST_MOBILE =
    'Seu carrinho foi limpo para continuar com produtos da Claro Móvel.';
  readonly mceHeaderLogoUrl =
    'https://mondrian.claro.com.br/brands/vertical/default/minha-claro-empresas.svg';
  readonly mceHeaderLogoInverseUrl =
    'https://mondrian.claro.com.br/brands/vertical/inverse/minha-claro-empresas.svg';

  private activeTopNavSubscription?: Subscription;
  private destroy$ = new Subject<void>();
  private channelData: ChannelData | null = null;
  private previousUtmSource: string | null = null;
 
  @ViewChild(CartPageComponent) cartPage?: CartPageComponent;

  isSidebarOpen$ = this.store.select(selectCartSidebarOpen);
  sidebarHeader$ = this.store.select(selectCartHeaderWithCount);
  totalCart$ = this.cart.getCartDisplayedTotal();
  isEmpty$ = this.store.select(selectIsCartEmpty);
  logo$ = this.channelConfigService.brands$.pipe(
    map(brands => this.channelConfigService.getHeaderLogo(brands))
  );
  logoInverse$ = this.channelConfigService.brands$.pipe(
    map(brands => this.channelConfigService.getInverseHeaderLogo(brands))
  );

  isHubMenuOpen = false;
  isLanguageMenuOpen = false;
  isMobileMenuOpen = false;
  isModalOpen = false;
  isLocationModalOpen = false;
  isExternalChannelExitModalOpen = false;
  isLocationMandatory = false;
  isMobileSearchOpen = false;
  isSolutionsMenuOpen = false;
  isAuthenticated = false;
  currentUser: User | null = null;
  searchTerm: string = '';
  screenWidth: number = typeof window !== 'undefined' ? window.innerWidth : 1024;

  get isMobileViewport(): boolean {
    return this.screenWidth < 1024;
  }

  activeTopNavItem = TOP_NAV_HUB_SOLUCOES;
  activeMainNavItem: string = '';
  isSolutionsMenuActive: boolean = false;
  userDocument: string = '';
  userCompanyName: string = '';

  /** Exibe apenas o endereço de instalação (modal/store). Não usa endereço da conta. */
  locationDisplayText$ = this.locationFacade.location$.pipe(
    map((loc) => {
      const formatCep = (raw: string) => {
        const digits = (raw || '').replace(/\D/g, '').slice(0, 8);
        return digits.length === 8 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : raw || '';
      };
      const cep = loc?.cep ? formatCep(loc.cep) : '';
      const hasCityState = !!(loc?.city && loc?.state);
      if (hasCityState) {
        const cityState = `${loc!.city}/ ${loc!.state}`;
        const cepPart = cep ? ` - Cep: ${cep}` : '';
        return { cityState, cepPart, hasLocation: true };
      }
      if (loc?.skipMindCatalog && cep) {
        return { cityState: `Cep: ${cep}`, cepPart: '', hasLocation: true };
      }
      return { cityState: '', cepPart: '', hasLocation: false };
    })
  );
  cnpjList: string[] = [];
  selectedCnpj: string = '';
  contractList: FixaResource[] = [];
  selectedContractId: string = '';
  isFixaMode: boolean = false;
  isCnpjLoading = true;
  isContractSelectionLoading = false;
  isAvailableOffersLoading = false;
  selectedSegment: 'mobile' | 'contract' = 'mobile';
  externalAccess: ExternalChannelAccessResponse | null = null;
  isComboSueNetsmsMode: boolean = false;
  isExternalChannelAccessMode = false;
  private pendingMobileSegmentSelection = false;

  get isSegmentSelectionLoading(): boolean {
    return this.isContractSelectionLoading || this.isAvailableOffersLoading;
  }

  get hideIndicationInUtm(): boolean {
    return this.isUtmContext();
  }

  get showLocationInHeader(): boolean {
    return this.isUtmContext() && this.hasMindCatalog;
  }

  get showExternalPanelBackButton(): boolean {
    return this.isAuthenticated && this.isExternalChannelAccessMode;
  }

  get isMceExternalFlow(): boolean {
    return isMceExternalChannelFlow(
      this.externalAccess,
      readStoredExternalChannelOrigin()
    );
  }

  get hasMindCatalog(): boolean {
    const catalogs = this.channelData?.catalogs;
    if (!Array.isArray(catalogs)) return false;
    return catalogs.some((c: string) => String(c).toUpperCase() === 'MIND');
  }

  private isUtmContext(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    try {
      const params = new URLSearchParams(window.location.search);
      const hasUtmInUrl = Array.from(params.keys()).some(k => k.toLowerCase().startsWith('utm_'));
      return hasUtmInUrl || this.utmPreservationService.isUtmEnabled();
    } catch {
      return this.utmPreservationService.isUtmEnabled();
    }
  }
  
  isSidebarOpen = false;
  sidebarHeader: SidebarHeader = {
    icon: 'person',
    title: 'Login',
  };

  // Configuração de idiomas
 languages = [
    {
      code: 'pt-BR',
      name: 'PT-BR',
      flag: 'assets/images/countries/brazil.svg',
      alt: 'Brasil' 
    },
    {
      code: 'en-US',
      name: 'EN-US',
      flag: 'assets/images/countries/usa.svg',
      alt: 'Estados Unidos'
    },
    {
      code: 'es-ES',
      name: 'ES-ES',
      flag: 'assets/images/countries/spain.svg', 
      alt: 'Espanha' 
    }
  ];

  selectedLanguage = this.languages[0];

  private rawCategories: Pick<ProductCategory, 'name'>[] = []; 

  private readonly categoryTranslationMap: Record<string, string> = {
    'Produtividade e Gestão': 'shared.categories.collaboration',
    'Presença Digital': 'shared.categories.digitalPresence',
    'Segurança': 'shared.categories.security',
    // 'Cloud': 'shared.categories.cloud',
    'Gestão e Benefícios Empresariais': 'shared.categories.management'
  };

  navigationItems: NavigationItem[] = [];

  get filteredNavigationItems(): NavigationItem[] {
    // Sempre retorna todas as categorias, sem filtro por largura da tela
    return this.navigationItems;
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      const savedValue = localStorage.getItem(this.SELECTED_CNPJ_KEY);
      if (savedValue) {
        const isCnpj = /^[A-Z0-9]{14}$/.test(unformatCnpj(savedValue));
        if (isCnpj) {
          this.selectedCnpj = this.normalizeCnpj(savedValue);
          this.selectedSegment = 'mobile';
        } else {
          this.selectedContractId = savedValue;
          this.selectedSegment = 'contract';
        }
        this.isCnpjLoading = false;
      }

      this.syncExternalChannelAccessFromSession();
    }

    this.locationFacade.location$
    .pipe(
      distinctUntilChanged((prev, curr) => {
        return (
          prev?.cep === curr?.cep &&
          prev?.addressNumber === curr?.addressNumber &&
          prev?.cnpj === curr?.cnpj
        );
      }),
      takeUntil(this.destroy$)
    )
    .subscribe(loc => {
      if (loc?.cep && loc?.addressNumber && !loc?.skipMindCatalog) {
        this.mindStore.loadCatalog(loc.cep, loc.addressNumber, loc?.cnpj);
      }
    });
    
    this.i18nService
      .getCurrentLanguage()
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(langCode => {
        const foundLang = this.languages.find(l => l.code === langCode);
        if (foundLang) {
          this.selectedLanguage = foundLang;
        }

        if (this.rawCategories.length > 0) {
          this.updateNavigationTranslations();
        }

        const catalogs = this.getChannelCatalogs();
        const hasMind = catalogs.includes('MIND');

        if (hasMind) {
          this.locationFacade.location$
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe(loc => {
              if (loc?.cep && loc?.addressNumber) {
                this.mindStore.loadCatalog(
                  loc.cep,
                  loc.addressNumber,
                  loc.cnpj
                );
              }
            });
        }
      });

    Promise.all([this.checkAndLoadChannelData(), this.loadCampaignNavigation()]).then(() => {
      this.campaignScopeReady = true;
      this.loadNavigationByCatalog();
      this.previousUtmSource = this.getCurrentUtmSource();
    });

    this.campaignScope.revision$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.loadCampaignNavigation().then(() => {
        this.campaignScopeReady = true;
        this.loadNavigationByCatalog();
      });
    });

    this.mindStore.catalog$
    .pipe(takeUntil(this.destroy$))
    .subscribe(() => {
      this.loadNavigationByCatalog();
    });

    this.availableOffersFacade.isLoading()
      .pipe(takeUntil(this.destroy$))
      .subscribe(isLoading => {
        const wasLoading = this.isAvailableOffersLoading;
        this.isAvailableOffersLoading = isLoading;

        if (this.pendingMobileSegmentSelection && wasLoading && !isLoading) {
          this.selectedSegment = 'mobile';
          this.pendingMobileSegmentSelection = false;
        }
      });

    this.route.queryParams.pipe(
      distinctUntilChanged((prev, curr) => {
        const prevUtm = prev['utm_source'] || null;
        const currUtm = curr['utm_source'] || null;
        return prevUtm === currUtm && (prev['campaign'] || null) === (curr['campaign'] || null);
      }),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.handleUtmChange();
    });

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      if (isPlatformBrowser(this.platformId)) {
        this.syncExternalChannelAccessFromSession();
      }
      setTimeout(() => {
        this.handleUtmChange();
      }, 100);
    });

    this.authSubscription = this.userFacade
      .getIsAuthenticated()
      .subscribe((isAuth: boolean) => {
        this.isAuthenticated = isAuth;
        if (isAuth) {
          this.userFacade.getUserUuid().pipe(take(1)).subscribe(uuid => {
            if (uuid) {
              this.userFacade.loadUserDetails(uuid);
            }
          });
        }
      });

    this.userFacade.getLoginData().subscribe((loginData) => {
      if (loginData && loginData.resources) {
        // Verificar se é login da fixa (resources são objetos) ou normal (resources são strings)
        const firstResource = loginData.resources[0];
        const isFixaLogin = firstResource && typeof firstResource === 'object' && 'customerAccountId' in firstResource;
        
        this.isFixaMode = Boolean(isFixaLogin);
        
        if (isFixaLogin) {
          // Login da fixa - resources são objetos FixaResource
          this.contractList = loginData.resources as FixaResource[];
          this.userCompanyName = loginData.companyName || '';

          // Preferir contrato já selecionado no store; senão, primeiro ativo
          this.userFacade.getSelectedContract().pipe(take(1)).subscribe(storeContract => {
            if (storeContract && this.contractList.some(c => c.customerAccountId === storeContract.customerAccountId)) {
              this.selectedContractId = storeContract.customerAccountId;
            } else {
              const activeContract = this.contractList.find(c => c.status === 'ACTIVE');
              const contractToSelect = activeContract || this.contractList[0];
              if (contractToSelect) {
                this.selectedContractId = contractToSelect.customerAccountId;
              }
            }
            if (this.selectedContractId && isPlatformBrowser(this.platformId)) {
              localStorage.setItem(this.SELECTED_CNPJ_KEY, this.selectedContractId);
            }
          });
        } else {
          // Login normal - resources são strings (CNPJs)
          const normalizedResources = (loginData.resources as string[])
            .filter(Boolean)
            .map((cnpj) => this.normalizeCnpj(cnpj));
          this.cnpjList = Array.from(new Set(normalizedResources));
        }
        this.isCnpjLoading = false;
      }
    });

    // Para COMBO_SUE_NETSMS, garantir que o header tenha os dados do response externo
    // para popular o seletor agrupado mesmo que loginData/resources não tenha os campos necessários.
    if (isPlatformBrowser(this.platformId)) {
      if (this.isComboSueNetsmsMode && this.externalAccess) {
        const doc = this.normalizeCnpj(this.externalAccess.userRequest?.document || '');
        if (doc) {
          this.selectedCnpj = this.selectedCnpj || doc;
          this.userDocument = doc;
          if (!this.cnpjList.includes(doc)) {
            this.cnpjList = Array.from(new Set([...this.cnpjList, doc]));
          }
        }
        if (Array.isArray(this.externalAccess.contracts)) {
          this.contractList = this.externalAccess.contracts;
        }
      }
    }

    this.userFacade.getUserDetails().pipe(
      filter(user => user !== null),
      distinctUntilChanged((prev, curr) => prev?.document === curr?.document)
    ).subscribe((user: User | null) => {
      this.currentUser = user;
      // Só processar dados do user se não estiver em modo fixa
      // No modo fixa, os dados vêm do loginData
      if (!this.isFixaMode && user && user.document) {
        const normalizedDocument = this.normalizeCnpj(user.document);
        this.userDocument = normalizedDocument;
        this.selectedCnpj = normalizedDocument;
        if (!this.cnpjList.some((cnpj) => this.normalizeCnpj(cnpj) === normalizedDocument)) {
          this.cnpjList = Array.from(new Set([...this.cnpjList, normalizedDocument]));
        }
        this.userCompanyName = user.business;
        this.isCnpjLoading = false;
      } else if (!this.isFixaMode) {
        this.isCnpjLoading = false;
      }
    });

     this.activeTopNavSubscription = this.headerService.activeTopNavItem$
    .subscribe((itemText: string) => {
      this.activeTopNavItem = itemText;
    });

    this.headerService.openMobileSearch$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.openMobileSearchPanel();
      });

    this.screenWidth = window.innerWidth;

    // Atualiza o item ativo baseado na rota atual e nas mudanças de rota
    this.updateActiveFromUrl(this.router.url);
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.updateActiveFromUrl(event.urlAfterRedirects || event.url);
      }
    });
  }


 private getCurrentUtmSource(): string | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }
    const urlParams = new URLSearchParams(window.location.search);
    const utmSourceFromUrl = urlParams.get('utm_source');
    
    if (utmSourceFromUrl) {
      return utmSourceFromUrl;
    }
    
    return this.utmPreservationService.preservedUtmParams?.['utm_source'] || null;
  }

  private handleUtmChange(): void {
    const currentUtmSource = this.getCurrentUtmSource();
    
    if (currentUtmSource !== this.previousUtmSource) {
      if (this.previousUtmSource && !currentUtmSource) {
        this.channelData = null;
      }
      
      Promise.all([this.checkAndLoadChannelData(), this.loadCampaignNavigation()]).then(() => {
        this.campaignScopeReady = true;
        this.loadNavigationByCatalog();
      });
      
      this.previousUtmSource = currentUtmSource;
    }
  }

  private checkAndLoadChannelData(): Promise<void> {
    return new Promise((resolve) => {
      if (!isPlatformBrowser(this.platformId)) {
        this.channelData = null;
        this.channelConfigService.setChannelData(null);
        resolve();
        return;
      }

      const utmSource = this.getCurrentUtmSource();

      if (!utmSource) {
        this.channelData = null;
        this.channelThemeService.resetToDefault();
        this.channelConfigService.setChannelData(null);
        resolve();
        return;
      }

      this.umanagerSlugService.checkSlugExists(utmSource).pipe(
        takeUntil(this.destroy$),
        take(1)
      ).subscribe({
        next: (response) => {
          if (response?.exists && utmSource) {
            this.umanagerSlugService.getChannelBySlug(utmSource).pipe(
              takeUntil(this.destroy$),
              take(1)
            ).subscribe({
              next: (channelData) => {
                if (channelData?.status && channelData.status !== 'ACTIVE') {
                  this.channelData = null;
                  this.utmPreservationService.disableUtmAndCleanUrl();
                  this.channelThemeService.resetToDefault();
                  this.channelConfigService.setChannelData(null);
                  resolve();
                  return;
                }

                this.channelData = channelData || null;
                const parsedColors = this.channelThemeService.parseChannelColors(
                  channelData?.colors ?? null
                );
                if (parsedColors) {
                  this.channelThemeService.applyChannelColors(parsedColors);
                } else {
                  this.channelThemeService.resetToDefault();
                }
                this.channelConfigService.setChannelData(channelData);

                const catalogs = channelData?.catalogs;
                const hasMind = Array.isArray(catalogs) && catalogs.some((c: string) => String(c).toUpperCase() === 'MIND');
                if (hasMind) {
                  this.locationFacade.location$.pipe(take(1)).subscribe(loc => {
                    if (!loc?.city || !loc?.state) {
                      this.isLocationModalOpen = true;
                      this.isLocationMandatory = true;
                    }
                  });
                }

                resolve();
              },
              error: (error) => {
                console.error('Error loading channel data:', error);
                resolve();
              }
            });
          } else {
            this.channelData = null;
            this.channelConfigService.setChannelData(null);
            resolve();
          }
        },
        error: (error) => {
          console.error('Error checking slug existence:', error);
          resolve();
        }
      });
    });
  }

  private loadNavigationItems(): void {
    this.productService.getCategories().pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: resp => {

        const filteredCategories =
          this.categoryHelper.filterAndSortCategories(
            resp.data,
            this.channelData?.categoriesUuid
          );

        this.rawCategories = categoriesForCampaign(filteredCategories, this.campaignCategoryUuids);
        this.scheduleNavigationTranslationWhenI18nReady();
      },
      error: error => {
        console.error('Error fetching categories for navigation:', error);
      }
    });
  }

  private scheduleNavigationTranslationWhenI18nReady(): void {
    if (!this.rawCategories.length) {
      return;
    }

    const locale = this.i18nService.getCurrentLanguageSync();
    this.i18nService
      .loadLanguage(locale)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(() => this.updateNavigationTranslations());
  }

  private updateNavigationTranslations(): void {
    this.navigationItems = this.rawCategories.map(category => {
      const translatedName = this.translateCategoryName(category.name);
      return {
        text: translatedName, 
        icon: 'arrow_forward_ios',
        link: sanitizeSlug(translatedName),
      };
    });
  }

  private translateCategoryName(categoryName: string): string {
    const translationKey = categoryName.startsWith('shared.categories.')
      ? categoryName
      : this.categoryTranslationMap[categoryName];

    return translationKey ? this.i18nService.instant(translationKey) : categoryName;
  }

  private getChannelCatalogs(): string[] {
    if (!this.channelData?.catalogs) return [];
    return this.channelData.catalogs.map((c: string) => c.toUpperCase());
  }
  private campaignScopeReady = false;
  private campaignCategoryUuids: string[] | null = null;
  private readonly campaignScope = inject(CampaignScopeService);

  private loadCampaignNavigation(): Promise<void> {
    return this.campaignScope.load().then(scope => {
      this.campaignCategoryUuids = scope?.categoryUuids.length ? scope.categoryUuids : null;
    });
  }

  private loadNavigationByCatalog(): void {
    if (!this.campaignScopeReady) {
      return;
    }

    const catalogs = this.channelData?.catalogs?.length
      ? this.getChannelCatalogs()
      : [];

    const hasSva = catalogs.includes('SVA');
    const hasMind = catalogs.includes('MIND');

    if (!this.channelData || catalogs.length === 0) {
      this.loadNavigationItems();
      return;
    }

    if (hasSva && hasMind) {
      this.getNavigationCombined();
      return;
    }

    if (hasSva) {
      this.loadNavigationItems();
      return;
    }

    if (hasMind) {
      this.getNavigationMind();
      return;
    }
  }

  private getNavigationMind(): void {
    if (this.campaignCategoryUuids) {
      this.rawCategories = [];
      this.navigationItems = [];
      return;
    }

    const catalog = this.mindStore.snapshot;

    if (!catalog?.groups?.length) {
      this.rawCategories = [];
      this.navigationItems = [];
      return;
    }

    this.rawCategories = catalog.groups
      .filter(group => group.items && group.items.length > 0) 
      .map(group => ({
        name: group.title
      }));
    this.scheduleNavigationTranslationWhenI18nReady();
  }

  private getNavigationCombined(): void {
    this.productService.getCategories().pipe(take(1)).subscribe({
      next: resp => {
        let svaCategories = categoriesForCampaign(resp.data, this.campaignCategoryUuids);

        if (this.channelData?.categoriesUuid?.length) {
          svaCategories = svaCategories.filter(cat =>
            this.channelData!.categoriesUuid.includes(cat.uuid)
          );
        }

        const catalog = this.mindStore.snapshot;

        const mindCategories = this.campaignCategoryUuids
          ? []
          : (catalog?.groups ?? [])
          .filter(group => group.items && group.items.length > 0)
          .map(group => ({
            name: group.title
          }));

        const merged = [
          ...svaCategories.map(c => ({ name: c.name })),
          ...mindCategories
        ];

        this.rawCategories = merged;
        this.scheduleNavigationTranslationWhenI18nReady();
      }
    });
  }

  onCnpjChange(newCnpj: string): void {
    if (this.isSegmentSelectionLoading) {
      return;
    }

    console.log('🔄 Mudando CNPJ de', this.selectedCnpj, 'para', newCnpj);
    const normalizedCnpj = this.normalizeCnpj(newCnpj);

    if (normalizedCnpj === this.selectedCnpj && this.selectedSegment === 'mobile') {
      return;
    }

    this.selectedCnpj = normalizedCnpj;
    if (this.selectedSegment === 'contract') {
      this.cart.clearExternalChannelCart();
      this.showSegmentCartClearedToast('mobile');
      this.pendingMobileSegmentSelection = true;
    } else {
      this.selectedSegment = 'mobile';
    }

    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(this.SELECTED_CNPJ_KEY, normalizedCnpj);
    }
    this.userFacade.selectCnpj(normalizedCnpj);
  }

  onContractChange(newContractId: string): void {
    if (this.isContractSelectionLoading) {
      return;
    }

    if (newContractId === this.selectedContractId && this.selectedSegment === 'contract') {
      return;
    }

    const previousContractId = this.selectedContractId;
    const previousSegment = this.selectedSegment;
    const previousStoredValue = isPlatformBrowser(this.platformId)
      ? localStorage.getItem(this.SELECTED_CNPJ_KEY)
      : null;

    console.log('🔄 Mudando contrato de', previousContractId, 'para', newContractId);
    this.selectedContractId = newContractId;
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(this.SELECTED_CNPJ_KEY, newContractId);
    }

    const selectedContract = this.contractList.find(
      contract => contract.customerAccountId === newContractId
    );

    if (!selectedContract) {
      console.warn('Contrato não encontrado na lista:', newContractId);
      this.selectedContractId = previousContractId;
      if (isPlatformBrowser(this.platformId)) {
        if (previousStoredValue) {
          localStorage.setItem(this.SELECTED_CNPJ_KEY, previousStoredValue);
        } else {
          localStorage.removeItem(this.SELECTED_CNPJ_KEY);
        }
      }
      return;
    }

    this.userFacade.setSelectedContract(selectedContract);
    this.isContractSelectionLoading = true;

    this.userFacade.getLoginData().pipe(take(1)).subscribe(loginData => {
      if (!loginData) {
        console.warn('Dados de login não disponíveis');
        this.isContractSelectionLoading = false;
        this.selectedContractId = previousContractId;
        this.selectedSegment = previousSegment;
        if (isPlatformBrowser(this.platformId)) {
          if (previousStoredValue) {
            localStorage.setItem(this.SELECTED_CNPJ_KEY, previousStoredValue);
          } else {
            localStorage.removeItem(this.SELECTED_CNPJ_KEY);
          }
        }
        return;
      }

      const request: SelectContractRequest = {
        operatorCode: selectedContract.operatorCode,
        customerAccountId: selectedContract.customerAccountId,
        document: loginData.document || '',
        sessionId: loginData.sessionId || ''
      };

      this.userService.selectContract(request)
        .pipe(
          catchError(error => {
            console.warn('Erro ao selecionar contrato:', error);
            this.toastService.showError(
              'Erro ao selecionar contrato. Tente novamente.',
              5000,
              'top-center'
            );
            this.selectedContractId = previousContractId;
            this.selectedSegment = previousSegment;
            if (isPlatformBrowser(this.platformId)) {
              if (previousStoredValue) {
                localStorage.setItem(this.SELECTED_CNPJ_KEY, previousStoredValue);
              } else {
                localStorage.removeItem(this.SELECTED_CNPJ_KEY);
              }
            }
            return of(null);
          }),
          finalize(() => {
            this.isContractSelectionLoading = false;
          })
        )
        .subscribe(response => {
          if (response) {
            applySelectContractToSession(response, this.authService, this.userFacade);
            this.availableOffersFacade.clearAvailableOffers();
            if (previousSegment !== 'contract') {
              this.cart.clearExternalChannelCart();
              this.showSegmentCartClearedToast('contract');
            }
            this.selectedSegment = 'contract';
          }
        });
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }
    if (this.activeTopNavSubscription) {
      this.activeTopNavSubscription.unsubscribe();
    }

  }

  onSidebarPrimaryClick(): void {
    this.cartPage?.onPrimaryCTA();
  }

  toggleHubMenu(): void {
    this.isHubMenuOpen = !this.isHubMenuOpen;
  }

  toggleLanguageMenu(): void {
    this.isLanguageMenuOpen = !this.isLanguageMenuOpen;
  }

  private normalizeCnpj(cnpj: string): string {
    return unformatCnpj(cnpj);
  }

  private showSegmentCartClearedToast(segment: 'mobile' | 'contract'): void {
    const message =
      segment === 'contract'
        ? this.SEGMENT_CART_CLEARED_TOAST_FIXA
        : this.SEGMENT_CART_CLEARED_TOAST_MOBILE;
    this.toastService.show(message, 'info', 5000, 'top-center');
  }

  toggleMobileMenu(): void {
    this.store.select(selectCartSidebarOpen).pipe(take(1)).subscribe(isCartOpen => {
      if (isCartOpen) this.cartService.closeCartSidebar();
      this.isMobileMenuOpen = !this.isMobileMenuOpen;
    });
  }

  toggleSolutionsMenu(): void {
    this.isSolutionsMenuOpen = !this.isSolutionsMenuOpen;
    if (this.isSolutionsMenuOpen) {
      this.isSolutionsMenuActive = true;
    } else {
      this.isSolutionsMenuActive = false;
    }
  }

  toggleCart(): void {
    if (this.isMobileMenuOpen) return;
    this.cartService.toggleCartSidebar();
  }

  closeMenuAndOpenCart(): void {
    this.isMobileMenuOpen = false;
    this.cartService.toggleCartSidebar();
  }

  onSearchClick(): void {
    if (this.isMobileSearchOpen) {
      this.closeMobileSearchPanel();
      return;
    }
    this.openMobileSearchPanel();
  }

  private openMobileSearchPanel(): void {
    this.isMobileSearchOpen = true;
    setTimeout(() => {
      const searchInput = document.querySelector(
        '.mobile-search-input'
      ) as HTMLInputElement;
      searchInput?.focus();
    }, 100);
  }

  private closeMobileSearchPanel(): void {
    this.isMobileSearchOpen = false;
    this.searchTerm = '';
  }

  onSearchSubmit(event: Event): void {
    const target = event.target as HTMLInputElement;
    const searchTerm = target.value.trim();
    if (searchTerm) {
      this.headerService.triggerSearch(searchTerm);
      this.isMobileSearchOpen = false;
      this.redirectToHomeIfNeeded();
    }
  }

  onSearchIconClick(): void {
    const searchTerm = this.searchTerm.trim();
    if (searchTerm) {
      this.headerService.triggerSearch(searchTerm);
      this.isMobileSearchOpen = false;
      this.redirectToHomeIfNeeded();
    }
  }

  private redirectToHomeIfNeeded(): void {
    if (this.router.url !== '/') {
      const queryParams = this.utmPreservationService.addPreservedUtmToQueryParams({
        ...(this.route.snapshot?.queryParams || {}),
      });
      this.router.navigate(['/'], { queryParams });
    }
  }

  onSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchTerm = target.value;
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.headerService.clearSearch();
  }

  onCartClick(): void {
    this.router.navigate(['/cart']);
  }

  onLoginClick(): void {
    const queryParams = this.utmPreservationService.addPreservedUtmToQueryParams({
      ...(this.route.snapshot?.queryParams || {}),
    });
    this.router.navigate(['/login'], { queryParams });
  }

  onHomeClick(): void {
    this.activeMainNavItem = 'Home';
    this.isSolutionsMenuActive = false;
    this.isSolutionsMenuOpen = false;
    this.navigateToHome();
  }

  onCategoriesClick(): void {
    this.activeMainNavItem = 'Categorias';
    this.isSolutionsMenuActive = false;
    this.isSolutionsMenuOpen = false;
    // Temporary: categories page not ready yet — both links go to Home until that screen ships.
    this.navigateToHome();
  }

  onMyAccountClick(): void {
    this.activeMainNavItem = 'Minha Conta';
    this.isSolutionsMenuActive = false; // Desmarca o dropdown
    this.isSolutionsMenuOpen = false; // Fecha o dropdown

    if (!this.isAuthenticated) {
      this.onLoginClick();
      return;
    }

    this.router.navigate(['/my-account']);
  }

  onMyOrdersClick(): void {
    this.activeMainNavItem = 'Meus Pedidos';
    this.isSolutionsMenuActive = false; // Desmarca o dropdown
    this.isSolutionsMenuOpen = false; // Fecha o dropdown
    this.router.navigate(['/my-account/orders']);
  }

  onIndicationClick(): void {
    // When user is "in the link" (UTM context), the Indication flow must not be available.
    if (!this.showIndiqueMenu) {
      this.isSolutionsMenuActive = false;
      this.isSolutionsMenuOpen = false;
      return;
    }

    this.activeMainNavItem = 'Indique uma PME';
    this.isSolutionsMenuActive = false; // Desmarca o dropdown
    this.isSolutionsMenuOpen = false; // Fecha o dropdown
    this.router.navigate(['/indica']);
  }

  onCadastroParceirosClick(): void {
    this.activeMainNavItem = 'Cadastro de Parceiros';
    this.isSolutionsMenuActive = false; // Desmarca o dropdown
    this.isSolutionsMenuOpen = false; // Fecha o dropdown
    this.router.navigate(['/cadastro-parceiros']);
  }

  onHelpCenterClick(): void {
    this.activeMainNavItem = 'Central de Ajuda';
    this.isSolutionsMenuActive = false; // Desmarca o dropdown
    this.isSolutionsMenuOpen = false; // Fecha o dropdown
    this.router.navigate(['/help-center']);
  }

  private updateActiveFromUrl(url: string): void {
    if (!url) {
      this.activeMainNavItem = '';
      return;
    }

    const path = url.split('?')[0];

    if (path === '/' || path === '') {
      this.activeMainNavItem = 'Home';
      this.isSolutionsMenuActive = false;
      return;
    }

    if (url.startsWith('/my-account')) {
      this.activeMainNavItem = 'Minha Conta';
      this.isSolutionsMenuActive = false;
      return;
    }

    if (url.startsWith('/indica')) {
      this.activeMainNavItem = 'Indique uma PME';
      this.isSolutionsMenuActive = false;
      return;
    }

    if (url.startsWith('/cadastro-parceiros')) {
      this.activeMainNavItem = 'Cadastro de Parceiros';
      this.isSolutionsMenuActive = false;
      return;
    }

    if (url.startsWith('/help-center')) {
      this.activeMainNavItem = 'Central de Ajuda';
      this.isSolutionsMenuActive = false;
      return;
    }

    // Qualquer outra rota limpa o destaque
    this.activeMainNavItem = '';
    this.isSolutionsMenuActive = false;
  }

  private navigateToHome(): void {
    const queryParams = this.utmPreservationService.addPreservedUtmToQueryParams({
      ...(this.route.snapshot?.queryParams || {}),
    });
    this.router.navigate(['/'], { queryParams });
    this.headerService.triggerScrollTo(null);
  }

  onLogoutClick(): void {
    this.clearExternalChannelSession();
    this.authService.logout();
    this.cart.clearCart();
    this.headerService.resetActiveTopNavItem();
    this.toastService.showSuccess(
      this.i18nService.instant('pages.registerMovel.messages.logoutSuccess'),
      3000,
      'top-center'
    );
    const queryParams = this.utmPreservationService.addPreservedUtmToQueryParams({
      ...(this.route.snapshot?.queryParams || {}),
    });
    this.router.navigate(['/'], { queryParams });
  }

  openExternalChannelExitModal(): void {
    this.isExternalChannelExitModalOpen = true;
  }

  closeExternalChannelExitModal(): void {
    this.isExternalChannelExitModalOpen = false;
  }

  confirmExternalChannelExit(): void {
    this.isExternalChannelExitModalOpen = false;
    this.clearExternalChannelSession();
    this.authService.logout();
    this.cart.clearCart();
    this.headerService.resetActiveTopNavItem();

    if (isPlatformBrowser(this.platformId)) {
      if (this.externalChannelParentMessageService.notifyHubClosed()) {
        return;
      }

      window.location.href = this.CLARO_EMPRESAS_HOME_URL;
      return;
    }

    this.router.navigate(['/']);
  }

  private syncExternalChannelAccessFromSession(): void {
    try {
      this.externalAccess = readExternalChannelAccessFromSession();
      if (this.externalAccess) {
        this.isComboSueNetsmsMode = this.externalAccess.type === 'COMBO_SUE_NETSMS';
        this.isExternalChannelAccessMode = true;
        return;
      }
    } catch {
      // ignore
    }

    this.externalAccess = null;
    this.isComboSueNetsmsMode = false;
    this.isExternalChannelAccessMode = false;
  }

  private clearExternalChannelSession(): void {
    const wasExternalChannelAccess = this.isExternalChannelAccessMode || !!this.externalAccess;

    this.externalAccess = null;
    this.isComboSueNetsmsMode = false;
    this.isExternalChannelAccessMode = false;

    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      sessionStorage.removeItem(STORAGE_EXTERNAL_CHANNEL_ACCESS);
      sessionStorage.removeItem(STORAGE_EXTERNAL_CHANNEL_ORIGIN);
      sessionStorage.removeItem(this.REDIRECT_AFTER_LOGIN_KEY);
      unblockCartHydration();
      if (wasExternalChannelAccess) {
        localStorage.removeItem(this.SELECTED_CNPJ_KEY);
      }
    } catch {
      // ignore
    }
  }

  selectLanguage(languageCode: string): void {
    if (this.selectedLanguage.code === languageCode) {
      this.isLanguageMenuOpen = false;
      return;
    }

    this.i18nService.setLanguage(languageCode).subscribe({
      next: () => {
        this.isLanguageMenuOpen = false;
      },
      error: (err) => {
        console.error('Erro ao mudar idioma:', err);
        this.isLanguageMenuOpen = false;
      }
    });
  }

  handleFontSize(_type: string) {
    this.isModalOpen = true;
  }

  onModalClose(): void {
    this.isModalOpen = false;
  }

  openLocationModal(): void {
    this.isLocationModalOpen = true;
    if (this.utmPreservationService.isUtmEnabled()) {
      this.locationFacade.location$.pipe(take(1)).subscribe((loc) => {
        const cnpj = loc?.cnpj ? unformatCnpj(loc.cnpj) : '';
        if (cnpj && cnpj.length === 14) {
          this.utmCnpjAccountService.checkAndStoreCnpjHasAccount(cnpj).subscribe();
        }
      });
    }
  }

  closeLocationModal(): void {
    this.isLocationModalOpen = false;
  }

  onLocationConfirm(data: LocationData): void {
    const cleanCep = data.cep?.replace(/\D/g, '') || undefined;
    const cleanCnpj = data.cnpj ? unformatCnpj(data.cnpj) : undefined;
    if (this.utmPreservationService.isUtmEnabled() && cleanCnpj && cleanCnpj.length === 14) {
      this.utmCnpjAccountService.checkAndStoreCnpjHasAccount(cleanCnpj).subscribe();
    }
    if (data.skipMindCatalog && cleanCep && data.addressNumber) {
      this.locationFacade.setLocation({
        city: '',
        state: '',
        cnpj: cleanCnpj,
        cep: cleanCep,
        addressNumber: data.addressNumber,
        skipMindCatalog: true
      });
      this.isLocationMandatory = false;
      this.closeLocationModal();
      return;
    }
    if (data.city && data.state) {
      this.locationFacade.setLocation({
        city: data.city,
        state: data.state,
        cnpj: cleanCnpj,
        cep: cleanCep,
        addressNumber: data.addressNumber ?? undefined,
        logradouro: data.logradouro,
        neighborhood: data.neighborhood
      });
      this.isLocationMandatory = false;
      this.closeLocationModal();
    }
  }

  private navigateToCategory(item: NavigationItem): void {
    const sanitizedRoute = sanitizeSlug(item.link);
    const currentQueryParams: Record<string, string> = {};
    if (isPlatformBrowser(this.platformId)) {
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.forEach((value, key) => {
        currentQueryParams[key] = value;
      });
    } else {
      Object.assign(currentQueryParams, this.route.snapshot.queryParams);
    }
    
    const navigationOptions: NavigationExtras = {};
    if (Object.keys(currentQueryParams).length > 0) {
      const queryParams = this.utmPreservationService.addPreservedUtmToQueryParams({ ...currentQueryParams });
      navigationOptions.queryParams = queryParams;
    }
    
    this.router.navigate([sanitizedRoute], navigationOptions);
    this.headerService.triggerScrollTo(item.text);
  }

  get showIndiqueMenu(): boolean {
    return this.channelConfigService.showIndiqueMenu; 
  }

  get showParceirosMenu(): boolean {
    return this.channelConfigService.showParceirosMenu; 
  }

  get isOnlyIndicationActive(): boolean {
    return this.onlyIndicationService.isOnlyIndicationActive;
  }

  onMobileNavItemClick(item: NavigationItem): void {
    this.navigateToCategory(item);
    this.isMobileMenuOpen = false;
  }

  onDesktopNavItemClick(item: NavigationItem): void {
    this.navigateToCategory(item);
    this.isSolutionsMenuOpen = false;
    this.isSolutionsMenuActive = false;
  }
  
  isCategoryActive(item: NavigationItem): boolean {
    const currentRoute = this.router.url;
    const sanitizedRoute = sanitizeSlug(item.text);
    return currentRoute.includes(sanitizedRoute);
  }

  onLogoClick(): void {
    if (this.isMceExternalFlow && isPlatformBrowser(this.platformId)) {
      if (this.externalChannelParentMessageService.notifyHubClosed()) {
        return;
      }

      window.location.href = this.CLARO_EMPRESAS_HOME_URL;
      return;
    }

    this.navigateToHome();
    // Limpar estados de menu ao voltar para home pelo logo
    this.activeTopNavItem = '';
    this.isSolutionsMenuActive = false;
    this.isSolutionsMenuOpen = false;
    this.isHubMenuOpen = false;
  }

  onTopNavItemClick(itemKey: string): void {
    this.activeTopNavItem = itemKey;
  }

  onDropdownNavItemClick(item: {link: string, text: string}): void {
    this.router.navigate([item.link]);
    this.activeTopNavItem = item.text;
    this.headerService.triggerScrollTo(null);
    this.isHubMenuOpen = false;
  }
    
  openLoginSidebar(): void {
    this.sidebarHeader = {
      icon: 'person',
      title: 'Login',
    };
    this.isSidebarOpen = true;
    this.sidebarService.openSidebar();
  }

  openRegisterSidebar(): void {
    this.sidebarHeader = {
      icon: 'person_add',
      title: 'Cadastro',
    };
    this.isSidebarOpen = true;
    this.sidebarService.openSidebar();
  }

  onSidebarClose(): void {
    this.store.dispatch(closeCartSidebar());
    this.sidebarService.closeSidebar();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event): void {
    this.screenWidth = (event.target as Window).innerWidth;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.language-selector')) {
      this.isLanguageMenuOpen = false;
    }
    if (
      !target.closest('.mobile-search-container') &&
      !target.closest('.search-btn') &&
      !target.closest('[data-mobile-search-trigger]')
    ) {
      this.isMobileSearchOpen = false;
    }
    if (!target.closest('.solutions-menu-container')) {
      this.isSolutionsMenuOpen = false;
      this.isSolutionsMenuActive = false; // Limpar estado ativo quando fechar
    }
  }

  testToggleTheme() {
    this.themeService.toggleTheme();
  }

}
